import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ViewEntityPage } from 'components/entityViewer/ViewEntityPage';
import { FCTransferDiscrepancyNotice } from 'components/warehouse/components/fcTransfers/discrepancies/FCTransferDiscrepancyNotice';
import { FCTransferEntityHeader } from 'components/warehouse/components/fcTransfers/entityView/FCTransferEntityHeader';
import { FCTransferEntityLineItemsEditor } from 'components/warehouse/components/fcTransfers/entityView/FCTransferEntityLineItemsEditor';
import { FCTransferInfoCard } from 'components/warehouse/components/fcTransfers/entityView/FCTransferInfoCard';
import { FCTransferBolDownloadModal } from 'components/warehouse/components/fcTransfers/FCTransferBolDownloadModal';
import { isFCTransferPendingStatus } from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';
import { FC_TRANSFER_DIRECTION } from 'components/warehouse/components/fcTransfers/FCTransferFilterConfig';
import {
    useDownloadFCTransferBol,
    useFCTransferById,
} from 'components/warehouse/components/fcTransfers/queries/FCTransferQueries';
import { useFCTransfersBackHref } from 'components/warehouse/components/fcTransfers/useFCTransfersBackHref';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';

/**
 * @param {Record<string, unknown> | null | undefined} transfer
 * @param {string | number | null | undefined} currentFacilityId
 * @returns {'OUTBOUND' | 'INBOUND'}
 */
function resolveDirectionForFacility(transfer, currentFacilityId) {
    if (transfer == null || currentFacilityId === undefined || currentFacilityId === null || currentFacilityId === '') {
        return FC_TRANSFER_DIRECTION.OUTBOUND;
    }
    const fid = String(currentFacilityId);
    const src = transfer.sourceFacilityId ?? transfer.source_facility_id;
    const dest = transfer.destinationFacilityId ?? transfer.destination_facility_id;
    if (src !== undefined && src !== null && String(src) === fid) {
        return FC_TRANSFER_DIRECTION.OUTBOUND;
    }
    if (dest !== undefined && dest !== null && String(dest) === fid) {
        return FC_TRANSFER_DIRECTION.INBOUND;
    }
    return FC_TRANSFER_DIRECTION.OUTBOUND;
}

/**
 * FC Transfer entity view
 */
export function ViewFCTransferEntity() {
    const { transferId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const { user } = useContext(UserContext);
    const currentFacilityId = user?.facilityID;
    const backHref = useFCTransfersBackHref();
    const isViewOnly = Boolean(location.state?.viewOnly);
    const backLabel =
        location.state?.fcTransfersListPath === '/warehouse/all-fc-transfers'
            ? 'Back to All FC Transfers'
            : 'Back to FC Transfers';

    const [lineItemsEditMode, setLineItemsEditMode] = useState(false);
    const [lineItemsStagingDirty, setLineItemsStagingDirty] = useState(false);
    const [isSavingLineItems, setIsSavingLineItems] = useState(false);
    const [isBolModalOpen, setIsBolModalOpen] = useState(false);
    const lineItemsEditorRef = useRef(null);

    const {
        data: transfer,
        error,
        isError,
        isLoading,
    } = useFCTransferById({
        transferId,
        enabled: Boolean(transferId),
    });
    const downloadFCTransferBolMutation = useDownloadFCTransferBol({
        onSuccess: () => {
            setIsBolModalOpen(false);
        },
        onError: (err) => {
            toast.error(err?.message ?? 'Failed to download transfer BOL');
        },
    });

    const operatorFacilityId = useMemo(() => {
        if (currentFacilityId === undefined || currentFacilityId === null || currentFacilityId === '') {
            return null;
        }
        const n = Number(currentFacilityId);
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [currentFacilityId]);

    const transferSourceFacilityId = useMemo(() => {
        if (transfer == null || typeof transfer !== 'object') return null;
        const raw = transfer.sourceFacilityId ?? transfer.source_facility_id;
        if (raw === undefined || raw === null || raw === '') return null;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [transfer]);

    const canEditOutgoingLineItems = useMemo(() => {
        if (isViewOnly) return false;
        if (!isFCTransferPendingStatus(transfer)) return false;
        if (operatorFacilityId == null || transferSourceFacilityId == null) return false;
        return operatorFacilityId === transferSourceFacilityId;
    }, [isViewOnly, transfer, operatorFacilityId, transferSourceFacilityId]);

    const direction = useMemo(
        () => resolveDirectionForFacility(transfer, currentFacilityId),
        [transfer, currentFacilityId],
    );

    const items = useMemo(() => {
        if (transfer && Array.isArray(transfer.items)) {
            return transfer.items;
        }
        return [];
    }, [transfer]);

    useEffect(() => {
        if (!isFCTransferPendingStatus(transfer)) {
            setLineItemsEditMode(false);
        }
    }, [transfer]);

    useEffect(() => {
        if (!canEditOutgoingLineItems && lineItemsEditMode) {
            lineItemsEditorRef.current?.resetStaging?.();
            setLineItemsEditMode(false);
        }
    }, [canEditOutgoingLineItems, lineItemsEditMode]);

    const wantsStartLineItemsEditFromList = Boolean(location.state?.startLineItemsEdit);

    /** Open line-item edit mode when navigated from list "Edit Transfer" (`location.state.startLineItemsEdit`). */
    useEffect(() => {
        if (!wantsStartLineItemsEditFromList) return;
        if (!transfer) return;

        // Strip `startLineItemsEdit` from history without dropping list filter context — a bare
        // `history.replace({ pathname, search })` clears `location.state` and loses `fcTransfersFilters`.
        const listNavState =
            typeof location.state?.fcTransfersFilters === 'string' && location.state.fcTransfersFilters.length > 0
                ? { fcTransfersFilters: location.state.fcTransfersFilters }
                : undefined;

        const replaceStrippingStartEditFlag = () => {
            history.replace({
                pathname: location.pathname,
                search: location.search,
                ...(listNavState ? { state: listNavState } : {}),
            });
        };

        if (!canEditOutgoingLineItems) {
            replaceStrippingStartEditFlag();
            return;
        }

        setLineItemsEditMode(true);
        replaceStrippingStartEditFlag();
    }, [
        transfer,
        canEditOutgoingLineItems,
        wantsStartLineItemsEditFromList,
        location.pathname,
        location.search,
        location.state,
        history,
    ]);

    const handleEnterLineItemsEdit = useCallback(() => {
        if (!canEditOutgoingLineItems) return;
        setLineItemsEditMode(true);
    }, [canEditOutgoingLineItems]);

    const handleCancelLineItemsEdit = useCallback(() => {
        lineItemsEditorRef.current?.resetStaging();
        setLineItemsEditMode(false);
    }, []);

    const handleSaveLineItemsEdit = useCallback(async () => {
        setIsSavingLineItems(true);
        try {
            const saved = await lineItemsEditorRef.current?.saveStagedChanges?.();
            if (saved) {
                setLineItemsEditMode(false);
            }
        } catch {
            // Error surfaced on the line items card
        } finally {
            setIsSavingLineItems(false);
        }
    }, []);

    const handleDownloadBol = useCallback(() => {
        if (!transfer?.id) return;
        setIsBolModalOpen(true);
    }, [transfer?.id]);

    const handleAcceptTransfer = useCallback(() => {
        if (!transfer?.id) return;
        const fcTransfersFilters =
            typeof location.state?.fcTransfersFilters === 'string' && location.state.fcTransfersFilters.length > 0
                ? location.state.fcTransfersFilters
                : undefined;
        const navState = fcTransfersFilters ? { fcTransfersFilters } : undefined;
        history.push(`/warehouse/accept-fc-transfer/${encodeURIComponent(String(transfer.id))}`, navState);
    }, [history, location.state, transfer?.id]);

    const handleBolModalOpenChange = useCallback(
        (isOpen) => {
            if (isOpen) return;
            if (downloadFCTransferBolMutation.isPending) return;
            setIsBolModalOpen(false);
        },
        [downloadFCTransferBolMutation.isPending],
    );

    const handleBolModalSubmit = useCallback(
        (bolFormData) => {
            if (!transfer?.id) return;
            downloadFCTransferBolMutation.mutate({
                transferId: transfer.id,
                bolFormData,
            });
        },
        [downloadFCTransferBolMutation, transfer?.id],
    );

    if (isLoading) {
        return (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4">
                <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground">Loading transfer…</p>
            </div>
        );
    }

    if (isError || !transfer) {
        return (
            <div className="flex flex-col gap-4 px-4 py-6">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Could not load transfer</AlertTitle>
                    <AlertDescription>{error?.message ?? 'Transfer not found or unavailable.'}</AlertDescription>
                </Alert>
                <Button variant="outline" asChild>
                    <Link to={backHref}>{backLabel}</Link>
                </Button>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col px-4 pb-8 pt-4">
            <ViewEntityPage
                header={
                    <FCTransferEntityHeader
                        backHref={backHref}
                        backLabel={backLabel}
                        canEditOutgoingLineItems={canEditOutgoingLineItems}
                        direction={direction}
                        isLineItemsEditMode={lineItemsEditMode}
                        isSavingLineItems={isSavingLineItems}
                        lineItemsSaveDisabled={!lineItemsStagingDirty}
                        transfer={transfer}
                        viewOnly={isViewOnly}
                        onAccept={handleAcceptTransfer}
                        onCancelLineItemsEdit={handleCancelLineItemsEdit}
                        onDownloadBol={handleDownloadBol}
                        onEditTransfer={handleEnterLineItemsEdit}
                        onSaveLineItemsEdit={handleSaveLineItemsEdit}
                    />
                }
                primaryInformationCard={<FCTransferInfoCard transfer={transfer} />}
                additionalInformationCards={
                    <>
                        <FCTransferEntityLineItemsEditor
                            ref={lineItemsEditorRef}
                            canEditLineItemsAtSource={canEditOutgoingLineItems}
                            editMode={lineItemsEditMode}
                            isSavingLineItems={isSavingLineItems}
                            items={items}
                            operatorFacilityId={currentFacilityId}
                            transfer={transfer}
                            onStagingDirtyChange={setLineItemsStagingDirty}
                        />
                        <div className="pt-2">
                            <FCTransferDiscrepancyNotice transfer={transfer} />
                        </div>
                    </>
                }
            />
            <FCTransferBolDownloadModal
                open={isBolModalOpen}
                transferId={transfer?.id ?? null}
                isSubmitting={downloadFCTransferBolMutation.isPending}
                onOpenChange={handleBolModalOpenChange}
                onSubmit={handleBolModalSubmit}
            />
        </div>
    );
}

ViewFCTransferEntity.displayName = 'ViewFCTransferEntity';
